import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCheck,
  faChevronDown,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineS, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { isGameStarted } from '@sorare/gameplay/src/lib/so5';

import {
  RivalsUserGroupGameFooter_footballRivalsGame,
  RivalsUserGroupGameFooter_rivalsUserGroup,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  background-color: var(--c-neutral-300);
  padding: var(--intermediate-unit) var(--double-unit);
`;

const MemberLineupNickname = styled(LabelS)`
  flex: 1;
`;

const LineupsInfoList = styled(Vertical)`
  padding-top: var(--intermediate-unit);
`;

const LineupSetStatus = styled(Horizontal)<{ set: boolean }>`
  color: ${({ set }) => (set ? 'var(--c-green-600)' : 'var(--c-red-600)')};
`;

const LineupScore = styled(Horizontal).attrs({ gap: 0.5 })`
  font: var(--t-label-s);
  align-items: baseline;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  transition: transform 0.2s;
  &.open {
    transform: rotate(180deg);
  }
`;

const Points = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: baseline;
  font: var(--t-label-m);
  color: var(--c-neutral-600);
  & > ${HeadlineS} {
    color: var(--c-neutral-1000);
  }
`;

export const Star = () => (
  <svg
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.25013 0.610107H6.75013L7.86545 4.04272L11.4747 4.04272L11.9382 5.4693L9.01829 7.59076L10.1336 11.0234L8.92008 11.905L6.00014 9.78358L3.0802 11.905L1.86667 11.0234L2.98198 7.59078L0.0620117 5.4693L0.525537 4.04272L4.13481 4.04272L5.25013 0.610107Z"
      fill="url(#paint0_linear_1457_46185)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1457_46185"
        x1="0.0620117"
        y1="11.905"
        x2="13.0131"
        y2="10.4971"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F8D3DA" />
        <stop offset="0" stopColor="#7724FF" />
        <stop offset="1" stopColor="#F3D0DD" />
      </linearGradient>
    </defs>
  </svg>
);

type Props = {
  userGroup: Nullable<RivalsUserGroupGameFooter_rivalsUserGroup>;
  footballRivalsGame: Nullable<RivalsUserGroupGameFooter_footballRivalsGame>;
  otherDailyGames: Nullable<RivalsUserGroupGameFooter_footballRivalsGame[]>;
  selected: boolean;
};

const useCollapsibleOpen = (selected: boolean) => {
  const [collapsibleOpen, setCollapsibleOpen] = useState<boolean | undefined>(
    undefined
  );
  const finalCollapsiableOpen =
    typeof collapsibleOpen === 'boolean' ? collapsibleOpen : selected;
  return [finalCollapsiableOpen, setCollapsibleOpen] as const;
};

const messages = defineMessages({
  points: {
    id: 'RivalsUserGroupGameFooter.points',
    defaultMessage: '<strong>{score}</strong> pts',
  },
});
export const RivalsUserGroupGameFooter = ({
  userGroup,
  footballRivalsGame,
  otherDailyGames,
  selected,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { nodes: lineups = [], totalCount: totalLineupsCount } =
    footballRivalsGame?.userGroupLineups || {};

  const { affiliate, memberships = [] } = userGroup || {};
  const [collapsibleOpen, setCollapsibleOpen] = useCollapsibleOpen(selected);
  const { game, myLineup } = footballRivalsGame || {};
  const isStarted = isGameStarted(game?.status);

  const orderedByScoreLineups = [...lineups].sort(
    (a, b) =>
      (b.scoreWithoutRarityBonus || 0) - (a.scoreWithoutRarityBonus || 0)
  );
  const bestScoreLineup = orderedByScoreLineups[0];

  const isUserBestDailyScore = (
    lineup: Nullable<
      NonNullable<
        RivalsUserGroupGameFooter_footballRivalsGame['userGroupLineups']['nodes']
      >[number]
    >
  ) => {
    const previousDailyLineups = otherDailyGames
      ?.map(g =>
        g.userGroupLineups.nodes?.find(
          l => l.manager.user.slug === lineup?.manager.user.slug
        )
      )
      .filter(Boolean);

    return previousDailyLineups?.every(
      l => l?.scoreWithoutRarityBonus < (lineup?.scoreWithoutRarityBonus || 0)
    );
  };

  const sortedMemberships = [...memberships].sort((a, b) => {
    const aLineup = lineups.find(
      lineup => lineup.manager.user.slug === a.user.slug
    );
    const bLineup = lineups.find(
      lineup => lineup.manager.user.slug === b.user.slug
    );
    return (
      (bLineup?.scoreWithoutRarityBonus || 0) -
      (aLineup?.scoreWithoutRarityBonus || 0)
    );
  });
  return (
    <Wrapper as={LinkBox}>
      <SBHorizontal
        as={LinkOverlay}
        onClick={() => {
          setCollapsibleOpen(v => !v);
        }}
      >
        {isStarted && (myLineup || !affiliate) ? (
          <Points>
            {isUserBestDailyScore(myLineup) && <Star />}
            {!myLineup && (
              <FormattedMessage
                id="RivalsUserGroupGameFooter.bestGroupScore"
                defaultMessage="Best group score:"
              />
            )}
            <FormattedMessage
              {...messages.points}
              values={{
                strong: (chunks: ReactNode[]) => (
                  <HeadlineS>{chunks}</HeadlineS>
                ),
                score:
                  myLineup?.scoreWithoutRarityBonus ||
                  bestScoreLineup?.scoreWithoutRarityBonus ||
                  0,
              }}
            />
          </Points>
        ) : (
          <LabelS>
            <FormattedMessage
              id="RivalsUserGroupGameFooter.lineupsSetCount"
              defaultMessage="{count, plural, one {# lineup set} other {# lineups set}}"
              values={{
                count: totalLineupsCount,
              }}
            />
          </LabelS>
        )}
        {!affiliate && (
          <StyledFontAwesomeIcon
            icon={faChevronDown}
            className={classNames({
              open: collapsibleOpen,
            })}
          />
        )}
      </SBHorizontal>
      {!affiliate && (
        <Collapse open={collapsibleOpen} hideGap>
          <LineupsInfoList>
            {sortedMemberships.map(({ id, user }) => {
              const isMe = user.slug === currentUser?.slug;
              const correspondingLineup = lineups.find(
                ({ manager }) => manager.user.slug === user.slug
              );

              if (!isMe && !correspondingLineup) {
                return null;
              }

              const top5 = isUserBestDailyScore(correspondingLineup);
              return (
                <Horizontal key={id}>
                  <Avatar key={id} user={user} rounded size={2.5} />
                  <MemberLineupNickname>{user.nickname}</MemberLineupNickname>
                  {isStarted ? (
                    <Horizontal center>
                      {top5 && <Star />}
                      <LineupScore>
                        {correspondingLineup ? (
                          <FormattedMessage
                            {...messages.points}
                            values={{
                              strong: (chunks: ReactNode[]) => (
                                <LabelM bold>{chunks}</LabelM>
                              ),
                              score:
                                correspondingLineup.scoreWithoutRarityBonus,
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </LineupScore>
                    </Horizontal>
                  ) : (
                    <LineupSetStatus set={!!correspondingLineup}>
                      <LabelS bold>
                        {correspondingLineup ? (
                          <FormattedMessage
                            id="RivalsUserGroupGameFooter.set"
                            defaultMessage="Set"
                          />
                        ) : (
                          <FormattedMessage
                            id="RivalsUserGroupGameFooter.notSet"
                            defaultMessage="Not set"
                          />
                        )}
                      </LabelS>
                      <FontAwesomeIcon
                        icon={correspondingLineup ? faCheck : faXmark}
                      />
                    </LineupSetStatus>
                  )}
                </Horizontal>
              );
            })}
          </LineupsInfoList>
        </Collapse>
      )}
    </Wrapper>
  );
};

RivalsUserGroupGameFooter.fragments = {
  footballRivalsGame: gql`
    fragment RivalsUserGroupGameFooter_footballRivalsGame on FootballRivalsGame {
      slug
      game {
        id
        status: statusTyped
      }
      myLineup {
        id
        scoreWithoutRarityBonus
        manager {
          id
          user {
            slug
          }
        }
      }
      userGroupLineups(slug: $slug, first: 20) {
        totalCount
        nodes @include(if: $includeLineups) {
          id
          scoreWithoutRarityBonus
          manager {
            id
            user {
              slug
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<RivalsUserGroupGameFooter_footballRivalsGame>,
  rivalsUserGroup: gql`
    fragment RivalsUserGroupGameFooter_rivalsUserGroup on RivalsUserGroup {
      slug
      affiliate
      memberships {
        id
        user {
          slug
          nickname
          ...Avatar_user
        }
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<RivalsUserGroupGameFooter_rivalsUserGroup>,
};
